import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Globe, Building2, ChevronDown } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  "Diesel (10ppm / 50ppm)",
  "Petrol (ULP 95 / 93)",
  "LPG",
  "CNG",
  "Gold (Au Bullion)",
  "Bauxite",
  "Coal",
];

const initial = {
  company_name: "",
  license_no: "",
  procurement_email: "",
  product_interest: "",
  monthly_volume: "",
  website: "", // honeypot — must remain empty for real users
};

const Contact = () => {
  const { t, locale } = useT();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.license_no || !form.procurement_email || !form.product_interest || !form.monthly_volume) {
      toast.error(t.contact.err_title, { description: t.contact.err_desc });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/trade-inquiries`, form, {
        headers: { "X-Vivasvan-Locale": locale },
      });
      toast.success(t.contact.ok_title, { description: t.contact.ok_desc });
      setForm(initial);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        const retry = err?.response?.headers?.["retry-after"];
        toast.error(t.contact.fail_title, {
          description: retry
            ? `Rate limit — retry in ${retry}s.`
            : (err?.response?.data?.detail || "Too many requests."),
        });
      } else {
        const msg = err?.response?.data?.detail || t.contact.fail_desc;
        toast.error(t.contact.fail_title, { description: typeof msg === "string" ? msg : t.contact.fail_desc });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-[#020C1B] border border-[#94A3B8]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-bone font-sans text-[15px] px-4 py-3 rounded-none transition-colors duration-200 placeholder:text-[#94A3B8]/50";

  const Field = ({ label, testId, children }) => (
    <label className="block">
      <span className="font-sans text-[10px] uppercase tracked text-[#D4AF37] block mb-2" style={{ letterSpacing: "0.28em" }} data-testid={`${testId}-label`}>
        {label}
      </span>
      {children}
    </label>
  );

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#020C1B]">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="hairline-crimson" />
              <span className="font-sans text-[11px] tracked uppercase text-[#D4AF37]" style={{ letterSpacing: "0.32em" }}>
                {t.contact.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-bone leading-[1.05] mb-5" style={{ fontWeight: 500 }}>
              {t.contact.h2a}<br />
              <span className="text-[#D4AF37] italic">{t.contact.h2b}</span>
            </h2>
            <p className="font-sans text-base md:text-lg text-steel leading-relaxed mb-10 max-w-lg">
              {t.contact.sub}
            </p>

            <div className="border border-[#D4AF37]/25 divide-y divide-[#D4AF37]/12">
              <div className="p-5 flex items-start gap-4">
                <Building2 size={18} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.26em" }}>{t.contact.entity_label}</div>
                  <div className="font-serif text-bone text-lg mt-1">{t.contact.entity_value}</div>
                </div>
              </div>
              <div className="p-5 flex items-start gap-4">
                <Globe size={18} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.26em" }}>{t.contact.portal_label}</div>
                  <a
                    href="https://www.vivasvanenergiesventures.co.za"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-portal-link"
                    className="font-serif text-bone text-lg mt-1 block hover:text-[#D4AF37] transition-colors break-all"
                  >
                    vivasvanenergiesventures.co.za
                  </a>
                </div>
              </div>
              <div className="p-5 flex items-start gap-4">
                <Mail size={18} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.26em" }}>{t.contact.desk_label}</div>
                  <a
                    href="mailto:mahesh@vivasvanenergiesventures.co.za"
                    data-testid="contact-email-link"
                    className="font-serif text-bone text-lg mt-1 block hover:text-[#D4AF37] transition-colors break-all"
                  >
                    mahesh@vivasvanenergiesventures.co.za
                  </a>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-[#0A192F] border border-[#D4AF37]/25 p-6 md:p-10 space-y-6"
            data-testid="trade-form"
            noValidate
          >
            <Field label={t.contact.field_company} testId="field-company">
              <input
                type="text"
                name="company_name"
                value={form.company_name}
                onChange={onChange}
                required
                className={inputClass}
                placeholder={t.contact.placeholder_company}
                data-testid="input-company-name"
              />
            </Field>

            <Field label={t.contact.field_license} testId="field-license">
              <input
                type="text"
                name="license_no"
                value={form.license_no}
                onChange={onChange}
                required
                className={inputClass}
                placeholder={t.contact.placeholder_license}
                data-testid="input-license"
              />
            </Field>

            <Field label={t.contact.field_email} testId="field-email">
              <input
                type="email"
                name="procurement_email"
                value={form.procurement_email}
                onChange={onChange}
                required
                className={inputClass}
                placeholder={t.contact.placeholder_email}
                data-testid="input-email"
              />
            </Field>

            <Field label={t.contact.field_product} testId="field-product">
              <div className="relative">
                <select
                  name="product_interest"
                  value={form.product_interest}
                  onChange={onChange}
                  required
                  className={`${inputClass} appearance-none pr-12 cursor-pointer`}
                  data-testid="select-product"
                >
                  <option value="" disabled>{t.contact.placeholder_product}</option>
                  {PRODUCTS.map((p) => (
                    <option key={p} value={p} style={{ background: "#020C1B" }}>{p}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
              </div>
            </Field>

            <Field label={t.contact.field_volume} testId="field-volume">
              <input
                type="text"
                name="monthly_volume"
                value={form.monthly_volume}
                onChange={onChange}
                required
                className={inputClass}
                placeholder={t.contact.placeholder_volume}
                data-testid="input-volume"
              />
            </Field>

            {/* Honeypot — invisible to humans, catches naive bots */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label>
                Website (leave blank)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange}
                  data-testid="input-honeypot"
                />
              </label>
            </div>

            <div className="pt-4 border-t border-[#D4AF37]/15">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-trade-request"
                className="btn-crimson group w-full inline-flex items-center justify-center gap-3 px-6 py-4 font-sans text-[13px] font-semibold uppercase tracked disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ letterSpacing: "0.24em", minHeight: 56 }}
              >
                {submitting ? t.contact.submit_pending : t.contact.submit}
                <Send size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 font-sans text-[11px] text-steel/80 leading-relaxed" data-testid="form-disclaimer">
                {t.contact.disclaimer}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
